/**
 * @file errorConfig.ts
 * @description
 * -----------------------------------------------------
 * Constantes relacionadas con los tipos de error
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-05-15
 * @modified 2025-12-11
 * @author DLTCode
 * @copyright © DLTCode
 * @license Private
 * */

import { Messages } from "next-intl";
import { themedAsset, ThemedAsset } from "@/constants/themedAsset";
import { HTTPStatus, HTTPStatusValue } from "@/constants/HttpStatus";

interface ErrorConfigEntry {
  imageSrc: ThemedAsset;
  titleKey: keyof Messages["Errors"]["ErrorPage"];
  messageKey: keyof Messages["Errors"]["ErrorPage"];
  altKey: keyof Messages["Errors"]["ErrorPage"];
  /** CSS class modifier for styling */
  cssModifier: string;
}

type ErrorConfigType = Partial<Record<HTTPStatusValue, ErrorConfigEntry>> & {
  default: ErrorConfigEntry;
};

/**
 * Configuración para cada tipo de error, indexada por código HTTP
 */
export const ERROR_CONFIG: ErrorConfigType = {
  [HTTPStatus.UNAUTHORIZED]: {
    imageSrc: themedAsset({
      path: "svg",
      filename: "dog-red",
      extension: "svg",
      hasDarkVariant: false,
    }),
    titleKey: "unauthorizedTitle",
    messageKey: "unauthorized",
    altKey: "unauthorizedAlt",
    cssModifier: "unauthorized",
  },
  [HTTPStatus.FORBIDDEN]: {
    imageSrc: themedAsset({
      path: "svg",
      filename: "dog-red",
      extension: "svg",
      hasDarkVariant: false,
    }),
    titleKey: "forbiddenTitle",
    messageKey: "forbidden",
    altKey: "forbiddenAlt",
    cssModifier: "forbidden",
  },
  [HTTPStatus.NOT_FOUND]: {
    imageSrc: themedAsset({
      path: "svg",
      filename: "Error-2",
      extension: "svg",
      hasDarkVariant: false,
    }),
    titleKey: "pageNotFoundTitle",
    messageKey: "pageNotFound",
    altKey: "pageNotFoundAlt",
    cssModifier: "page_not_found",
  },
  [HTTPStatus.GONE]: {
    imageSrc: themedAsset({
      path: "svg",
      filename: "Error_401",
      extension: "svg",
      hasDarkVariant: false,
    }),
    titleKey: "resourceNotFoundTitle",
    messageKey: "reportNotFound",
    altKey: "resourceNotFoundAlt",
    cssModifier: "resource_not_found",
  },
  [HTTPStatus.INTERNAL_SERVER_ERROR]: {
    imageSrc: themedAsset({
      path: "svg",
      filename: "dog-red",
      extension: "svg",
      hasDarkVariant: false,
    }),
    titleKey: "serverErrorTitle",
    messageKey: "serverError",
    altKey: "serverErrorAlt",
    cssModifier: "server_error",
  },
  [HTTPStatus.SERVICE_UNAVAILABLE]: {
    imageSrc: themedAsset({
      path: "svg",
      filename: "dog-red",
      extension: "svg",
      hasDarkVariant: false,
    }),
    titleKey: "serviceUnavailableTitle",
    messageKey: "serviceUnavailable",
    altKey: "serviceUnavailableAlt",
    cssModifier: "service_unavailable",
  },
  default: {
    imageSrc: themedAsset({
      path: "svg",
      filename: "dog-red",
      extension: "svg",
      hasDarkVariant: false,
    }),
    titleKey: "genericErrorTitle",
    messageKey: "genericError",
    altKey: "genericErrorAlt",
    cssModifier: "generic",
  },
};

/** Helper to get error config by status code */
export const getErrorConfig = (status?: HTTPStatusValue): ErrorConfigEntry => {
  if (status && status in ERROR_CONFIG) {
    return ERROR_CONFIG[status]!;
  }
  return ERROR_CONFIG.default;
};
